import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useToastMessage } from '@/Providers/ToastMessageProvider'
import { Icons } from '@/components/icons'
import {
    Card,
    CardHeader,
    CardBody,
    Divider,
    Button,
    Link
} from '@/components/nextui'
import { useAuthHook } from '@/hooks/useAuthHook'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useHomologationsApi } from '@/services/api/homologationsApi'
import { FormsProps } from '@/types/forms/formStatus'

interface ProjectionPdfViewerProps {
    isLoading: boolean
    pdfSrc: string
    onRetry: () => void
}

const ProjectionPdfViewer = ({
    isLoading,
    pdfSrc,
    onRetry
}: ProjectionPdfViewerProps): JSX.Element => {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center w-full h-full">
                <Icons.Spinner className="text-primary w-12 h-12" />
            </div>
        )
    }

    if (pdfSrc) {
        return (
            <iframe
                key={pdfSrc}
                title="PDF de Proyección"
                src={pdfSrc}
                className="w-full h-full"
            />
        )
    }

    return (
        <div className="flex flex-col items-center justify-center w-full h-full p-8 text-center">
            <Icons.InfoCircle className="w-10 h-10 text-secondary mb-4" />
            <h3 className="text-lg font-semibold mb-2">
                Aún no hay proyección disponible
            </h3>
            <p className="text-sm text-gray-600 max-w-xl mb-6">
                Tu solicitud de homologación sigue en evaluación. Te avisaremos
                por correo electrónico cuando tu proyección esté lista. Por
                favor, vuelve a intentarlo más tarde.
            </p>
            <div className="flex items-center gap-3">
                <Button
                    color="primary"
                    onPress={onRetry}
                    isDisabled={isLoading}
                >
                    Reintentar
                </Button>
                <Link
                    isExternal
                    href="https://universidadean.edu.co/homologacion"
                    className="text-primary"
                >
                    Conoce más sobre la homologación
                </Link>
            </div>
        </div>
    )
}

export const ProjectionModule = ({
    onNext,
    onPrevious,
    idLastForm,
    completed
}: FormsProps): JSX.Element => {
    const { t } = useTranslation()
    const { idInscription, idAspirant } = useAuthHook()
    const homologationsApi = useHomologationsApi()
    const aspirantsApi = useAspirantsApi()
    const { showMessage } = useToastMessage()

    const [pdfUrl, setPdfUrl] = useState<string | null>(null)
    const [pdfSrc, setPdfSrc] = useState<string>('')
    const currentBlobUrl = useRef<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const handleError = (): void => {
        setPdfUrl(null)
        showMessage({
            message: t('errorLoadingDocument') || 'Error cargando documento',
            type: 'error'
        })
    }

    const getDocumentNumber = async (): Promise<string | null> => {
        if (!idAspirant) {
            return null
        }

        const basicDataResponse = await aspirantsApi.getBasicData(
            Number(idAspirant)
        )

        return (basicDataResponse.data as any)?.documentNumber || null
    }

    const fetchPdfUrl = async (): Promise<void> => {
        try {
            setIsLoading(true)
            const documentNumber = await getDocumentNumber()

            if (!documentNumber) {
                handleError()

                return
            }

            const response = await homologationsApi.getFinalActByDocument(
                String(documentNumber),
                idInscription ? Number(idInscription) : undefined
            )

            if (response.status === 200 && response.data) {
                setPdfUrl(
                    typeof response.data === 'string' ? response.data : null
                )
            } else {
                handleError()
            }
        } catch (_err) {
            handleError()
        } finally {
            setIsLoading(false)
        }
    }

    const cacheBustedUrl = useMemo(() => {
        if (!pdfUrl) {
            return ''
        }
        const sep = pdfUrl.includes('?') ? '&' : '?'

        return `${pdfUrl}${sep}_=${Date.now()}`
    }, [pdfUrl])

    useEffect(() => {
        let cancelled = false

        const loadBlob = async () => {
            if (!cacheBustedUrl) {
                setPdfSrc('')
                setIsLoading(false)

                return
            }
            setIsLoading(true)
            try {
                const res = await fetch(cacheBustedUrl, { cache: 'no-store' })

                if (!res.ok) {
                    throw new Error('failed')
                }
                const blob = await res.blob()
                const url = URL.createObjectURL(blob)

                currentBlobUrl.current = url
                if (!cancelled) {
                    setPdfSrc(url)
                }
            } catch (e) {
                console.warn('Falling back to direct URL for PDF:', e)
                if (!cancelled) {
                    setPdfSrc(cacheBustedUrl)
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                }
            }
        }

        if (pdfUrl) {
            void loadBlob()
        } else {
            setPdfSrc('')
            setIsLoading(false)
        }

        return () => {
            cancelled = true
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current)
                currentBlobUrl.current = ''
            }
        }
    }, [cacheBustedUrl])

    useEffect(() => {
        void fetchPdfUrl()
    }, [])

    return (
        <section className="space-y-6">
            <Card className="shadow-md">
                <CardHeader className="flex items-center gap-2">
                    <Icons.FileCheck className="text-secondary" />
                    <span className="font-semibold">
                        Proyección de la homologación
                    </span>
                </CardHeader>
                <Divider />
                <CardBody>
                    <div className="w-full h-[70vh] border rounded-lg overflow-hidden bg-white">
                        <ProjectionPdfViewer
                            isLoading={isLoading}
                            pdfSrc={pdfSrc}
                            onRetry={fetchPdfUrl}
                        />
                    </div>
                </CardBody>
            </Card>
        </section>
    )
}
